// BITÁCORA OPERATIVA INSTITUCIONAL — memoria operativa del centro (read-model `bitacora-operativa-1`).
// Sólo lectura: casos, eventos, estados e historial autorizado, con el scope aplicado EN SERVIDOR
// sobre el sitio_id PERSISTIDO de cada documento. Aquí no se toma, cierra ni modifica nada.
// UX-P1: cada registro se puede ABRIR (panel lateral en escritorio, pantalla completa en móvil) y,
// si es un caso, consultar su expediente disponible (CaseDetailV3 readOnly) y su reporte (REPORT-01).
import { useEffect, useState, useCallback, useMemo, useRef } from "react";
import { createPortal } from "react-dom";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { verboEvento } from "@/lib/consolaV3/meta";
import { fmtTimestampSeg, fmtHoraCorta, fmtTimestamp } from "@/lib/format";
import CaseDetailV3 from "@/components/consolaV3/CaseDetailV3";
import ClipEvento from "@/components/eventos/ClipEvento";
import { ExportarBitacora } from "@/components/reportes/ReporteControles";
import {
  ScrollText, Search, Loader2, Inbox, ChevronDown, ChevronRight, User, MapPin, X, Clock,
} from "lucide-react";

const CLASES = [
  { id: "", label: "Todo" },
  { id: "casos", label: "Casos" },
  { id: "eventos", label: "Eventos" },
];
const PRIORIDADES = ["alta", "media", "baja"];

const TONO = {
  alta: "bg-[#FE6A55]/15 text-[#C2410C]",
  media: "bg-amber-100 text-amber-800",
  baja: "bg-slate-200 text-slate-600",
};

const useNow = () => {
  const [n, s] = useState(Date.now());
  useEffect(() => { const t = setInterval(() => s(Date.now()), 1000); return () => clearInterval(t); }, []);
  return n;
};

function Item({ it, onAbrir }) {
  return (
    <li data-testid={`bitop-item-${it.id}`} data-clase={it.clase}
      className="rounded-xl border border-slate-200 bg-white dark:border-slate-700 dark:bg-slate-900/50">
      <button type="button" data-testid={`bitop-abrir-${it.id}`} onClick={() => onAbrir(it)}
        aria-label={`Abrir ${it.clase === "caso" ? "el caso" : "el evento"} ${verboEvento(it.tipo)}`}
        className="flex w-full items-start gap-2 rounded-xl px-3.5 py-3 text-left transition-colors hover:bg-slate-50 dark:hover:bg-slate-800/60">
        <span className="min-w-0 flex-1">
          <span className="flex flex-wrap items-center gap-x-2 gap-y-1">
            <span data-testid={`bitop-hora-${it.id}`} className="font-mono text-xs tabular-nums text-slate-500">
              {fmtHoraCorta(it.hora)}
            </span>
            <span className="text-[11px] text-slate-400">{fmtTimestampSeg(it.hora)}</span>
            <span className={`rounded px-1.5 py-0.5 text-[10px] font-bold uppercase ${it.clase === "caso" ? "bg-[#01CBE3]/20 text-[#0891A6]" : "bg-slate-200 text-slate-600"}`}>
              {it.clase === "caso" ? "Caso" : "Evento"}
            </span>
            {it.prioridad && (
              <span data-testid={`bitop-prioridad-${it.id}`} className={`rounded px-1.5 py-0.5 text-[10px] font-bold uppercase ${TONO[it.prioridad] || TONO.baja}`}>
                {it.prioridad_label || it.prioridad}
              </span>
            )}
            <span data-testid={`bitop-estado-${it.id}`} className="rounded bg-slate-100 px-1.5 py-0.5 text-[10px] font-bold uppercase text-slate-600 dark:bg-slate-800 dark:text-slate-300">
              {it.estado_label}
            </span>
          </span>

          <span className="mt-1 block text-sm font-extrabold uppercase leading-tight text-slate-800 dark:text-slate-100">
            {verboEvento(it.tipo)}
          </span>
          <span className="mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-xs text-slate-500 dark:text-slate-300">
            <span className="inline-flex items-center gap-1">
              <User className="h-3 w-3" />
              <span data-testid={`bitop-persona-${it.id}`}>{it.persona_nombre || it.identidad_motivo || "Sin residente asociado"}</span>
            </span>
            <span className="inline-flex items-center gap-1">
              <MapPin className="h-3 w-3" />{[it.sitio_nombre, it.habitacion].filter(Boolean).join(" · ") || "—"}
            </span>
            {it.responsable && <span data-testid={`bitop-responsable-${it.id}`}>· {it.responsable}</span>}
          </span>

          {(it.historial || []).length > 1 && (
            <span data-testid={`bitop-historial-${it.id}`} className="mt-1.5 block space-y-0.5 border-l-2 border-slate-100 pl-2.5 dark:border-slate-700">
              {it.historial.map((h, i) => (
                <span key={i} className="block text-[11px] text-slate-500 dark:text-slate-400">
                  <span className="font-mono tabular-nums">{fmtHoraCorta(h.hora)}</span> · {h.label}
                  {h.actor ? ` · ${h.actor}` : ""}
                </span>
              ))}
            </span>
          )}
        </span>
        <ChevronRight className="mt-1 h-4 w-4 shrink-0 text-slate-300" />
      </button>
    </li>
  );
}

// Detalle de EVENTO: exclusivamente los datos autoritativos que publica el read-model para ese
// evento. No se inventa caso, atención ni reporte individual.
function EventoDetalle({ it, onAbrirCaso }) {
  return (
    <div data-testid="bitop-evento-detalle" className="space-y-3.5">
      <div className="rounded-xl border-2 border-slate-200 bg-slate-50 p-5 dark:border-slate-700 dark:bg-slate-900/60">
        <span data-testid="bitop-evento-badge"
          className={`inline-flex items-center rounded px-2 py-0.5 text-[11px] font-bold uppercase tracking-wider ${it.caso_id ? "bg-[#01CBE3]/20 text-[#0891A6]" : "bg-slate-200 text-slate-600"}`}>
          {it.caso_id ? "Evento · asociado a caso" : "Evento · sin caso"}
        </span>
        <h2 className="mt-2 break-words text-2xl font-extrabold tracking-tight text-[#012472] dark:text-white">
          {verboEvento(it.tipo)}
        </h2>
        <p className="mt-1 text-sm text-slate-600 dark:text-slate-300">
          {it.persona_nombre || it.identidad_motivo || "Sin residente asociado"}
        </p>
      </div>
      <div className="space-y-2 rounded-xl border border-slate-200 bg-white p-4 text-sm dark:border-slate-700 dark:bg-slate-900/50">
        <p className="flex items-center gap-2 text-slate-600 dark:text-slate-300">
          <MapPin className="h-4 w-4 text-slate-400" />
          {[it.sitio_nombre, it.habitacion].filter(Boolean).join(" · ") || "Ubicación no consta"}
        </p>
        <p className="flex items-center gap-2 text-slate-600 dark:text-slate-300">
          <Clock className="h-4 w-4 text-slate-400" /> Recibido {fmtTimestamp(it.hora)}
        </p>
        <p className="text-xs uppercase tracking-wide text-slate-400">Estado: {it.estado_label}</p>
        {it.caso_id ? (
          <div className="pt-1 space-y-2">
            <button type="button" data-testid="bitop-evento-ver-caso" onClick={() => onAbrirCaso(it.caso_id)}
              className="inline-flex min-h-11 items-center gap-1.5 rounded-lg bg-[#012472]/5 px-3 py-1.5 text-xs font-semibold text-[#012472] transition-colors hover:bg-[#012472]/10 dark:bg-slate-800 dark:text-slate-100">
              Abrir el caso asociado <ChevronRight className="h-3.5 w-3.5" />
            </button>
            <p className="text-xs text-slate-400">Este evento está asociado a un caso: su atención y su reporte se consultan en el caso.</p>
          </div>
        ) : (
          <p className="pt-1 text-xs text-slate-400">
            Este evento no generó caso. Se muestra sólo con su información autoritativa disponible.
          </p>
        )}
      </div>
      {/* CLIPS-HM-01 — el clip del evento, en la misma superficie donde ya se consulta el evento. */}
      <ClipEvento eventoId={it.id} />
    </div>
  );
}

export default function BitacoraOperativa() {
  const nowMs = useNow();
  const [params] = useSearchParams();
  const [items, setItems] = useState([]);
  const [cursor, setCursor] = useState(null);
  const [hayMas, setHayMas] = useState(false);
  const [cargando, setCargando] = useState(false);
  const [clase, setClase] = useState("");
  const [prioridad, setPrioridad] = useState("");
  const [busqueda, setBusqueda] = useState("");
  const [termino, setTermino] = useState("");
  const [sel, setSel] = useState(null);            // { kind:'caso', id } | { kind:'evento', it }
  const [caseDetail, setCaseDetail] = useState(null);
  const [errorCaso, setErrorCaso] = useState(null);
  const [sitioExport, setSitioExport] = useState("");
  const personaId = params.get("persona_id") || "";
  const sitioId = params.get("sitio_id") || "";
  const pedidoRef = useRef(0);

  const cargar = useCallback(async (cur = null) => {
    setCargando(true);
    try {
      const { data } = await api.get("/bitacora-operativa", {
        params: {
          ...(sitioId ? { sitio_id: sitioId } : {}),
          ...(personaId ? { persona_id: personaId } : {}),
          ...(clase ? { clase } : {}),
          ...(prioridad ? { prioridad } : {}),
          ...(termino ? { q: termino } : {}),
          ...(cur ? { cursor: cur } : {}),
          limite: 50,
        },
      });
      setItems((prev) => (cur ? [...prev, ...(data.items || [])] : (data.items || [])));
      setHayMas(!!data.hay_mas);
      setCursor(data.cursor_siguiente || null);
    } catch (e) {
      setItems([]); setHayMas(false); setCursor(null);
      // El error se enuncia tal cual: no se simula una lectura completa.
      console.warn(formatApiErrorDetail(e));
    } finally { setCargando(false); }
  }, [sitioId, personaId, clase, prioridad, termino]);

  useEffect(() => { cargar(null); }, [cargar]);

  // Sitios presentes en la bitácora consultada: un reporte = un sitio. Si la vista reúne varios,
  // se elige cuál exportar; con uno solo se preselecciona.
  const sitiosVista = useMemo(() => {
    const m = new Map();
    for (const it of items) if (it.sitio_id) m.set(it.sitio_id, it.sitio_nombre || it.sitio_id);
    return [...m].map(([id, nombre]) => ({ id, nombre }));
  }, [items]);
  useEffect(() => {
    if (sitioId) { setSitioExport(sitioId); return; }
    if (sitiosVista.length === 1) setSitioExport(sitiosVista[0].id);
    else if (sitioExport && !sitiosVista.some((s) => s.id === sitioExport)) setSitioExport("");
  }, [sitioId, sitiosVista]); // eslint-disable-line react-hooks/exhaustive-deps

  const abrirCaso = useCallback(async (id) => {
    if (!id) return;
    const pedido = ++pedidoRef.current;
    setSel({ kind: "caso", id });
    setCaseDetail(null);
    setErrorCaso(null);
    try {
      const { data } = await api.get(`/alarmas/${id}`);
      if (pedidoRef.current === pedido) setCaseDetail(data);
    } catch (e) {
      if (pedidoRef.current !== pedido) return;
      const detalle = formatApiErrorDetail(e?.response?.data?.detail)
        || "No se pudo abrir el expediente de este caso.";
      setErrorCaso(detalle);
      toast.error(detalle);
    }
  }, []);

  const abrir = (it) => {
    if (it.clase === "caso") { abrirCaso(it.caso_id || it.id); return; }
    pedidoRef.current += 1;
    setCaseDetail(null); setErrorCaso(null);
    setSel({ kind: "evento", it });
  };
  const cerrar = () => { pedidoRef.current += 1; setSel(null); setCaseDetail(null); setErrorCaso(null); };

  return (
    <div data-testid="bitacora-operativa" className="tema-noche-auto space-y-4 pb-24 md:pb-6">
      <div>
        <h1 className="inline-flex items-center gap-2 text-2xl font-extrabold tracking-tight text-[#012472] dark:text-white">
          <ScrollText className="h-6 w-6 text-[#01CBE3]" strokeWidth={2} /> Bitácora operativa
        </h1>
        <p className="mt-1 text-sm text-slate-500 dark:text-slate-300">
          Casos, eventos y su historial, en hora local de Tijuana. Cada registro conserva el sitio
          en el que ocurrió: nada se reatribuye al centro actual de un residente o de un equipo.
        </p>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <form onSubmit={(e) => { e.preventDefault(); setTermino(busqueda.trim()); }}
          className="flex min-w-0 flex-1 items-center gap-2">
          <div className="relative min-w-0 flex-1">
            <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <input data-testid="bitop-busqueda" value={busqueda} onChange={(e) => setBusqueda(e.target.value)}
              placeholder="Buscar por residente o tipo de hecho"
              className="w-full rounded-lg border border-slate-200 bg-white py-2.5 pl-8 pr-3 text-sm text-slate-700 outline-none focus:border-[#01CBE3] dark:border-slate-600 dark:bg-slate-900 dark:text-slate-100" />
          </div>
          <button data-testid="bitop-buscar" type="submit"
            className="min-h-11 rounded-lg bg-[#012472] px-3.5 text-sm font-bold text-white hover:bg-[#012472]/90">Buscar</button>
        </form>
      </div>

      <div className="flex flex-wrap items-center gap-1.5">
        {CLASES.map((c) => (
          <button key={c.id || "todo"} data-testid={`bitop-clase-${c.id || "todo"}`} onClick={() => setClase(c.id)}
            className={`min-h-9 rounded-lg px-2.5 text-xs font-bold transition-colors ${clase === c.id ? "bg-[#012472] text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200 dark:bg-slate-800 dark:text-slate-300"}`}>
            {c.label}
          </button>
        ))}
        <span className="mx-1 h-5 w-px bg-slate-200 dark:bg-slate-700" />
        {PRIORIDADES.map((p) => (
          <button key={p} data-testid={`bitop-prioridad-filtro-${p}`} onClick={() => setPrioridad(prioridad === p ? "" : p)}
            className={`min-h-9 rounded-lg px-2.5 text-xs font-bold uppercase transition-colors ${prioridad === p ? "bg-[#012472] text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200 dark:bg-slate-800 dark:text-slate-300"}`}>
            {p}
          </button>
        ))}
      </div>

      {/* REPORT-01 — exportación por SITIO y periodo. El backend consulta TODOS los registros
          elegibles del periodo: no se exporta «lo que se ve». La búsqueda libre de pantalla no
          viaja al reporte porque REPORT-01 no la admite, y así se declara. */}
      {!sitioId && sitiosVista.length > 1 && (
        <label className="flex max-w-xs flex-col gap-1 text-xs font-medium text-slate-500">
          Sitio del reporte
          <select data-testid="bitop-sitio-export" value={sitioExport}
            onChange={(e) => setSitioExport(e.target.value)}
            aria-label="Elegir el sitio de la exportación"
            className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700 dark:border-slate-600 dark:bg-slate-900 dark:text-slate-100">
            <option value="">Elige un sitio para exportar</option>
            {sitiosVista.map((s) => <option key={s.id} value={s.id}>{s.nombre}</option>)}
          </select>
        </label>
      )}
      <ExportarBitacora sitioId={sitioExport}
        sitioNombre={(sitiosVista.find((s) => s.id === sitioExport) || {}).nombre}
        personaId={personaId} prioridad={prioridad} clase={clase} />
      {termino && (
        <p data-testid="bitop-aviso-busqueda" className="-mt-2 text-xs text-slate-400">
          El texto buscado («{termino}») filtra esta pantalla, no el reporte: la exportación usa
          fechas, sitio, residente, prioridad y tipo de registro.
        </p>
      )}

      {cargando && items.length === 0 && (
        <p data-testid="bitop-cargando" className="inline-flex items-center gap-2 text-sm text-slate-400">
          <Loader2 className="h-4 w-4 animate-spin" /> Cargando…
        </p>
      )}

      {!cargando && items.length === 0 && (
        <p data-testid="bitop-vacio" className="inline-flex items-center gap-2 rounded-xl bg-slate-50 px-3 py-2.5 text-sm text-slate-500 dark:bg-slate-900">
          <Inbox className="h-4 w-4" /> No hay registros operativos para estos criterios.
        </p>
      )}

      <ul data-testid="bitop-lista" className="space-y-2">
        {items.map((it) => <Item key={`${it.clase}-${it.id}`} it={it} onAbrir={abrir} />)}
      </ul>

      {hayMas && (
        <button data-testid="bitop-cargar-mas" onClick={() => cargar(cursor)} disabled={cargando}
          className="inline-flex min-h-11 items-center gap-1.5 rounded-lg border border-[#01CBE3]/60 px-3.5 text-sm font-bold text-[#0891A6] hover:bg-[#01CBE3]/10 disabled:opacity-50">
          {cargando ? <Loader2 className="h-4 w-4 animate-spin" /> : <ChevronDown className="h-4 w-4" />} Cargar más
        </button>
      )}

      {/* Panel de detalle EN PORTAL AL BODY (el shell anima `transform` y rompería `position:fixed`).
          Escritorio: panel lateral. Móvil: ocupa la pantalla completa. Sólo lectura. */}
      {sel && createPortal(
        <div className="fixed inset-0 z-50 flex justify-end" data-testid="bitop-panel">
          <div className="absolute inset-0 bg-black/40 dark:bg-black/25 dark:backdrop-blur-[2px]" onClick={cerrar} />
          <div className="tema-noche-auto relative z-10 h-full w-full max-w-full overflow-y-auto bg-[#F1F5F9] shadow-2xl sm:max-w-[560px] sm:border-l sm:border-transparent dark:border-[#263B5D] dark:bg-slate-950">
            <div className="sticky top-0 z-10 flex items-center justify-between border-b border-slate-200 bg-white px-4 py-3 dark:border-slate-700 dark:bg-slate-900">
              <p className="text-sm font-bold text-[#012472] dark:text-white">
                {sel.kind === "evento" ? "Detalle del evento" : "Detalle del caso"}
              </p>
              <button data-testid="bitop-panel-cerrar" onClick={cerrar} aria-label="Cerrar el detalle"
                className="rounded-lg p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-700">
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="p-4">
              {sel.kind === "evento" ? (
                <EventoDetalle it={sel.it} onAbrirCaso={abrirCaso} />
              ) : errorCaso ? (
                <p data-testid="bitop-caso-error" className="rounded-xl bg-white p-4 text-sm text-slate-600 dark:bg-slate-900 dark:text-slate-300">
                  {errorCaso}
                </p>
              ) : caseDetail ? (
                <CaseDetailV3 detail={caseDetail} nowMs={nowMs} busy={false} demo={false} readOnly
                  permitirReporteIncidente
                  onTake={() => {}} onCloseClick={() => {}} onLiberar={() => {}}
                  onSolicitarApoyo={() => {}} onTransition={() => {}} onAtenderTambien={() => {}}
                  takeBlockedAlta={false} />
              ) : (
                <p data-testid="bitop-caso-cargando" className="p-6 text-sm text-slate-400">Cargando detalle…</p>
              )}
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}
